import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from "@mui/material";

interface SessionTimeoutWarningProps {
    onExtendSession?: () => void;
    onLogout?: () => void;
    isWarning?: boolean;
    timeRemaining?: number;
}

export const SessionTimeoutWarning = ({
    onExtendSession,
    onLogout,
    isWarning = false,
    timeRemaining = 0,
}: SessionTimeoutWarningProps) => {
    const { t } = useTranslation();

    // Format seconds as MM:SS
    const formatTime = (totalSeconds: number): string => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const handleExtendSession = () => {
        if (onExtendSession) {
            onExtendSession();
        }
    };

    const handleLogout = () => {
        if (onLogout) {
            onLogout();
        }
    };

    if (!isWarning) {
        return null;
    }

    return (
        <Dialog
            open={isWarning}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            data-testid="session-timeout-warning"
        >
            <DialogTitle
                sx={{
                    textAlign: "center",
                    pb: 2,
                    fontSize: "1.5rem",
                    fontWeight: "bold"
                }}
            >
                {t("sessionTimeout.warning.title")}
            </DialogTitle>
            <DialogContent>
                <Typography
                    variant="body1"
                    gutterBottom
                    sx={{
                        textAlign: "center",
                        mb: 3,
                        fontSize: "1.1rem"
                    }}
                >
                    {t("sessionTimeout.warning.message")}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        variant="h2"
                        component="div"
                        sx={{
                            fontSize: "3rem",
                            fontWeight: "bold",
                            color: "error.main",
                            textAlign: "center",
                            fontFamily: "monospace",
                            backgroundColor: "grey.100",
                            px: 3,
                            py: 2,
                            borderRadius: 2,
                            minWidth: "140px",
                            border: "2px solid",
                            borderColor: "error.light",
                        }}
                        aria-live="polite"
                        aria-label={t("sessionTimeout.warning.countdown.label", { time: formatTime(Math.floor(timeRemaining / 1000)) })}
                    >
                        {formatTime(Math.floor(timeRemaining / 1000))}
                    </Typography>
                </Box>

            </DialogContent>
            <DialogActions sx={{ padding: "16px 24px", px: 3, py: 2 }}>
                <Button
                    onClick={handleLogout}
                    variant="text"
                    disableRipple
                    disableElevation
                    data-testid="session-timeout-warning-logout-button"
                    sx={{
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.action.hover,
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                    }}
                >
                    {t("sessionTimeout.actions.logout")}
                </Button>
                <Button
                    onClick={handleExtendSession}
                    variant="contained"
                    color="primary"
                    disableElevation
                    disableRipple
                    data-testid="session-timeout-warning-extend-button"
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: (theme) => theme.palette.brand.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.brand.dark,
                            color: (theme) => theme.palette.primary.contrastText,
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                    }}
                >
                    {t("sessionTimeout.actions.extend")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
